import { ReactNode } from 'react';

// Types pour les variantes de boutons
export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline';

// Interface pour les actions/boutons
export interface GenericStatusPageAction {
  /** Texte affiché sur le bouton */
  label: string;
  /** Route de navigation (optionnel) */
  to?: string;
  /** Fonction callback (optionnel) */
  onClick?: () => void;
  /** Variante de style du bouton */
  variant?: ButtonVariant;
  /** Icône du bouton (emoji ou composant React) */
  icon?: ReactNode;
  /** Désactiver le bouton */
  disabled?: boolean;
}

// Props principales du composant GenericStatusPage
export interface GenericStatusPageProps {
  /** Code de statut à afficher (ex: "404", "500") */
  statusCode?: string;
  /** Titre principal de la page */
  title?: string;
  /** Message secondaire */
  message?: string;
  /** Description détaillée */
  description?: string;
  /** Icône à afficher (emoji ou composant React) */
  icon?: ReactNode;
  /** Classe Tailwind pour la couleur de l'icône */
  iconColor?: string;
  /** Tableau des actions/boutons à afficher */
  actions?: GenericStatusPageAction[];
  /** Afficher le bouton de retour par défaut */
  showBackButton?: boolean;
  /** Classes CSS supplémentaires pour le conteneur */
  containerClassName?: string;
  /** Contenu personnalisé à afficher */
  children?: ReactNode;
}

// Types pour les pages de statut spécifiques
export interface NotFoundPageProps {
  /** URL de redirection personnalisée */
  redirectTo?: string;
  /** Actions supplémentaires */
  additionalActions?: GenericStatusPageAction[];
}

export interface UnauthorizedPageProps {
  /** Message personnalisé */
  customMessage?: string;
  /** Email de contact du support */
  supportEmail?: string;
  /** Permissions requises (pour debugging) */
  requiredPermissions?: string[];
}

export interface ServerErrorPageProps {
  /** Détails de l'erreur */
  error?: Error;
  /** ID de l'erreur pour le tracking */
  errorId?: string;
  /** Fonction de retry personnalisée */
  onRetry?: () => void;
}

export interface SuccessPageProps {
  /** Titre personnalisé */
  title?: string;
  /** Message personnalisé */
  message?: string;
  /** URL de redirection */
  redirectTo?: string;
  /** Label du bouton de redirection */
  redirectLabel?: string;
  /** Délai de redirection automatique (en ms) */
  autoRedirectDelay?: number;
}

export interface MaintenancePageProps {
  /** Durée estimée de la maintenance */
  estimatedDuration?: string;
  /** URL du statut du service */
  statusUrl?: string;
  /** Heure de fin prévue */
  endTime?: Date;
}

// Enum pour les types d'erreur courants
export enum ErrorType {
  NOT_FOUND = 'NOT_FOUND',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  SERVER_ERROR = 'SERVER_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  MAINTENANCE = 'MAINTENANCE'
}

// Configuration par défaut pour chaque type d'erreur
export interface ErrorConfig {
  statusCode?: string;
  title: string;
  message: string;
  description?: string;
  icon: ReactNode;
  iconColor: string;
  defaultActions?: GenericStatusPageAction[];
}

export type ErrorConfigMap = Record<ErrorType, ErrorConfig>;
